import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardHeader,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button,
    Alert,
    AlertTitle,
    Divider,
    Grid,
    Paper
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const ApiExposureWorkflow: React.FC = () => {
    return (
        <Box id="workflow-expose-api" sx={{ scrollMarginTop: '80px' }}>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <SecurityIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">Exposing APIs and Configuring Client Access</Typography>
                        </Box>
                    }
                    action={
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => {
                                const element = document.getElementById('workflow-navigation');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                }
                            }}
                        >
                            Back to Workflows
                        </Button>
                    }
                />
                <Divider />
                <CardContent>
                    <Typography variant="body2" paragraph>
                        This workflow guides you through exposing an API in one application, preauthorizing other applications to
                        use it, and configuring client applications to consume the API.
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Prerequisites:</Typography>
                        <List dense disablePadding>
                            <ListItem>
                                <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                <ListItemText primary="An existing application registration for your API (Web API or Web App)" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                <ListItemText primary="An application registration for your client app (optional - can be created during this workflow)" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                <ListItemText primary="Admin permissions to create and modify application registrations" />
                            </ListItem>
                        </List>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 1: Expose an API in Your Application
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                First, you need to configure your application to expose an API:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Navigate to Applications and select your API application"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Open the Expose an API section"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Set the Application ID URI if not already defined"
                                        secondary="This is the unique identifier for your API, typically in the format api://[client-id] or a custom domain name"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click on Add a scope to define access permissions for your API"
                                    />
                                </ListItem>
                            </List>

                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, my: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Defining Scopes</Typography>
                                <Typography variant="body2">
                                    When adding a scope, provide the following information:
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                        <ListItemText
                                            primary="Scope name: A short identifier (e.g., read, write, user_impersonation)"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                        <ListItemText
                                            primary="Admin consent display name: Descriptive name shown to admins (e.g., 'Read user data')"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                        <ListItemText
                                            primary="Admin consent description: Detailed explanation of the permission"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                        <ListItemText
                                            primary="User consent settings: Whether users can consent to this permission"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                        <ListItemText
                                            primary="State: Set to 'Enabled'"
                                        />
                                    </ListItem>
                                </List>
                            </Box>

                            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                                <AlertTitle>About Application ID URIs</AlertTitle>
                                <Typography variant="body2">
                                    The Application ID URI functions as a unique identifier for your API in the Microsoft identity platform.
                                    It's used when requesting tokens for this API and should be globally unique. For multi-tenant
                                    applications, consider using your verified domain name to ensure uniqueness.
                                </Typography>
                            </Alert>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 2: Preauthorize Client Applications
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Preauthorizing applications allows specific client applications to access your API without requiring
                                user or admin consent:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="In the Expose an API section, find 'Authorized client applications'"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'Add a client application'"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Enter the Client ID of the application you want to preauthorize"
                                        secondary="This is the Application (client) ID of the client app that will access your API"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Select which API scopes this client application should be authorized to access"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'Add application' to save your changes"
                                    />
                                </ListItem>
                            </List>

                            <Box sx={{ p: 2, bgcolor: '#f5f9ff', borderRadius: 1, my: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Understanding Preauthorization</Typography>
                                <Typography variant="body2" paragraph>
                                    Preauthorization creates a trust relationship between your API and specific client applications.
                                    When an application is preauthorized:
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Users aren't prompted for consent when the client app requests tokens for your API"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Administrators don't need to grant consent separately"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="The client app can request access tokens for your API with appropriate scopes"
                                        />
                                    </ListItem>
                                </List>
                            </Box>

                            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                                <AlertTitle>Security Consideration</AlertTitle>
                                <Typography variant="body2">
                                    Only preauthorize applications that you trust. Preauthorized applications can request tokens
                                    for your API without additional consent prompts, so ensure you're only granting this access
                                    to applications you control or fully trust.
                                </Typography>
                            </Alert>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 3: Understanding the "Validate Permissions" Button
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                In AppConfig², the "Validate Permissions" button helps verify API permission configurations:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Select your API application in the Applications list"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Navigate to the API Permissions tab"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click the 'Validate Permissions' button"
                                    />
                                </ListItem>
                            </List>

                            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                                This validation process checks:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="That all requested API permissions are properly configured"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="If admin consent has been granted for permissions requiring it"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Whether the application has appropriate credentials configured (for client credential flows)"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="That all referenced APIs and permissions exist and are accessible"
                                    />
                                </ListItem>
                            </List>

                            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                                The validation results provide:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Status indicators for each permission"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Specific error messages for misconfigured permissions"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Recommendations to fix identified issues"
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 4: Configure a Client Application to Use Your API
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Now, configure a client application to consume your API:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Navigate to Applications and select your client application (or create a new one)"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Navigate to 'API permissions'"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'Add a permission'"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Select 'My APIs' tab"
                                        secondary="Here you'll see all APIs exposed by applications in your tenant"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Select your API application from the list"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Choose whether you need Delegated or Application permissions:"
                                    />
                                </ListItem>
                            </List>

                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, my: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Permission Types</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                                            <Typography variant="subtitle2" gutterBottom>Delegated Permissions</Typography>
                                            <Typography variant="body2">
                                                Used when your app accesses the API on behalf of a signed-in user.
                                                The effective permissions are the intersection of user permissions
                                                and what your app is authorized to do.
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                                            <Typography variant="subtitle2" gutterBottom>Application Permissions</Typography>
                                            <Typography variant="body2">
                                                Used when your app runs as a background service without a signed-in user.
                                                These permissions often require admin consent and grant full access
                                                at the requested permission level.
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </Box>

                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Select the specific permissions your client app needs"
                                        secondary="Choose from the scopes you defined when exposing your API"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Click 'Add permissions' to save your changes"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="If required, click 'Grant admin consent for [tenant]'"
                                        secondary="This step is necessary for application permissions and any delegated permissions that require admin consent"
                                    />
                                </ListItem>
                            </List>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <AlertTitle>Testing API Access</AlertTitle>
                                <Typography variant="body2" paragraph>
                                    To verify that your client app can successfully access your API:
                                </Typography>
                                <List dense sx={{ pl: 2 }}>
                                    <ListItem>
                                        <ListItemText primary="1. Use the App Tester to acquire a token for your client application" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText primary="2. Ensure you request the correct scopes in the token request" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText primary="3. Examine the resulting token to verify it contains the expected scopes" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText primary="4. Use this token to make a request to your API" />
                                    </ListItem>
                                </List>
                            </Alert>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 5: Implementing API Access in Your Code
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Finally, implement API access in your application code:
                            </Typography>

                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>For the API Application:</Typography>
                            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, my: 2, fontFamily: 'monospace', overflow: 'auto' }}>
                                <pre style={{ margin: 0 }}>
                                    {`// Node.js example with Express and Microsoft Identity Web
const express = require('express');
const msal = require('@azure/msal-node');
const { validateJwt } = require('@azure/msal-express-wrapper');

const app = express();

// Middleware to validate access tokens
app.use(validateJwt({
  audience: 'api://your-client-id', // Your API's Application ID URI
  issuer: 'https://login.microsoftonline.com/your-tenant-id/v2.0'
}));

// Protected API endpoint
app.get('/api/data', (req, res) => {
  // Access token has been validated
  // You can check for specific scopes if needed:
  if (req.authInfo.scopes.includes('api://your-client-id/read')) {
    res.json({ message: "API data returned successfully" });
  } else {
    res.status(403).json({ error: "Insufficient permissions" });
  }
});

app.listen(3000);`}
                                </pre>
                            </Box>

                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>For the Client Application:</Typography>
                            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, my: 2, fontFamily: 'monospace', overflow: 'auto' }}>
                                <pre style={{ margin: 0 }}>
                                    {`// Browser-based client example using MSAL.js
import { PublicClientApplication } from '@azure/msal-browser';

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: 'your-client-app-id',
    authority: 'https://login.microsoftonline.com/your-tenant-id',
    redirectUri: 'http://localhost:3000'
  }
};

const msalInstance = new PublicClientApplication(msalConfig);

// Request API access
async function callApi() {
  const tokenRequest = {
    scopes: ['api://api-client-id/read'] // Scope from your exposed API
  };

  try {
    // Get token silently if user is already signed in
    const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
    
    // Call the API with the token
    const response = await fetch('https://your-api-url/api/data', {
      headers: {
        'Authorization': \`Bearer \${tokenResponse.accessToken}\`
      }
    });
    
    const data = await response.json();
    console.log(data);
  } catch (error) {
    // Fall back to interactive sign in if needed
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenPopup(tokenRequest);
    }
    console.error(error);
  }
}`}
                                </pre>
                            </Box>

                            <Box sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 1, my: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Key Development Considerations:</Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Always verify tokens on the API side"
                                            secondary="Check audience, issuer, expiration, and required scopes"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Handle token acquisition failures gracefully"
                                            secondary="Implement proper error handling and renewal logic"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Use Microsoft Authentication Libraries (MSAL)"
                                            secondary="These libraries handle token acquisition, caching, and renewal automatically"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Implement granular permission checks"
                                            secondary="Check for specific scopes/permissions for each API operation"
                                        />
                                    </ListItem>
                                </List>
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ApiExposureWorkflow;